import { createSlice } from "@reduxjs/toolkit";
import {
  fetchBluebonnet,
  fetchForecastUsage,
  fetchPremiseDetails,
  fetchTduUsage,
  fetchUtilityCode,
  getCompetitors,
  getScreenContent,
  updateCustomer,
} from "src/api/APIs";
import { CompetitorType } from "src/components/BullseyeGraph";
import { PremiseType } from "src/components/types/address";
import { BKVPlanType, GimmickyType } from "src/components/types/plans";
import { ViewTypes } from "src/components/widgets/UsageModal";
import { AppThunk } from "src/store";
import Notibar from "src/utils/Notibar";
import initialize from "src/utils/initialize";

const premise_init = {
  lot_size: "0",
  bedrooms: "0",
  bathrooms: "0",
};
export interface AddressType {
  label: string;
  value: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  esiid: string;
  premise_type: string;
  [key: string]: any;
}

export interface UtilityType {
  utility_id: string;
  utility_name: string;
  utility_code: string;
  tdu_base: string;
  tdu_per: string;
  zipcode: string;
}

interface UsageInfoType {
  type: "forecast" | "tdu" | "smt";
  annual: number;
  month_avg: number;
}

interface ContentValue {
  value: any[];
  [key: string]: any;
}

interface GraphButton {
  showButton: any;
  showRateGraph: any;
}

interface BKVStates {
  appLoading: boolean;
  zipcode: number;
  address: AddressType;
  usage: { [key: string]: number };
  usageInfo: UsageInfoType;
  loading: boolean;
  view: ViewTypes;
  type: string;
  content: { value: ContentValue };
  allPlans: { [key: string]: BKVPlanType[] };
  bluebonnet: BKVPlanType;
  gimmicky: GimmickyType;
  competitorArray: CompetitorType[];
  premise: PremiseType;
  utilities: { [key: string]: UtilityType };
  utilityCode: string;
  utilityID: string;
  elementLoading: any;
  showBaseGraphButton: boolean;
  showRateGraph: boolean;
}

const initialState: BKVStates = {
  appLoading: true,
  zipcode: null,
  address: null,
  usage: {},
  usageInfo: { type: null, annual: 0, month_avg: 0 },
  loading: false,
  elementLoading: {},
  view: "options",
  content: null,
  type: null,
  allPlans: {},
  bluebonnet: null,
  gimmicky: null,
  competitorArray: [],
  premise: premise_init,
  utilities: {},
  utilityCode: null,
  utilityID: null,
  showBaseGraphButton: false,
  showRateGraph: true,
};

const slice = createSlice({
  name: "bkv",
  initialState,
  reducers: {
    setLoading(state, payload) {
      state.loading = payload.payload;
      return state;
    },
    setAppLoading(state, payload) {
      state.appLoading = payload.payload;
      return state;
    },
    setElementLoading(state, payload) {
      state.elementLoading = { ...state.elementLoading, ...payload.payload };
      return state;
    },
    setZIPcode(state: BKVStates, payload) {
      state.zipcode = payload.payload;
      return state;
    },
    setAddress(state: BKVStates, payload) {
      state.address = payload.payload;
      return state;
    },
    setUsage(state: BKVStates, payload) {
      state.usage = payload.payload;
      return state;
    },
    setView(state: BKVStates, payload) {
      state.view = payload.payload;
      return state;
    },
    setUsageInfo(state: BKVStates, payload: { payload: UsageInfoType }) {
      state.usageInfo = payload.payload;
      return state;
    },
    setContent(state: BKVStates, payload) {
      state.content = payload.payload.content;
      state.type = payload.payload.type;
      return state;
    },
    setAllPlans(state: BKVStates, payload) {
      state.allPlans = payload.payload;
      return state;
    },
    setBluebonnet(state: BKVStates, payload) {
      state.bluebonnet = payload.payload;
      return state;
    },
    setGimmicky(state: BKVStates, payload) {
      state.gimmicky = payload.payload;
      return state;
    },
    setPremiseDetails(state: BKVStates, payload) {
      state.premise = payload.payload;
      return state;
    },
    setUtilities(state: BKVStates, payload) {
      // let _utilities = { ...payload.payload.utilities, [state.zipcode]: payload.payload }
      state.utilities[payload.payload.zipcode] = payload.payload.utilities;
      state.utilityCode = payload.payload.utilities["utility_code"];
      state.utilityID = payload.payload.utilities["utility_id"];
      return state;
    },
    setUtilityCode(state: BKVStates, payload) {
      state.utilityCode = payload.payload;
      return state;
    },
    setCompetitorArray(state: BKVStates, payload) {
      state.competitorArray = payload.payload;
      return state;
    },
    setShowBaseGraphButton(state: BKVStates, payload) {
      state.showBaseGraphButton = payload.payload;
      return state;
    },
    setShowShowRateGraph(state: BKVStates, payload) {
      state.showRateGraph = payload.payload;
      return state;
    },
  },
});

/**
 * Function to get all customers based on the parameters
 * @param page : Page Number
 * @param perPage  : records per page
 * @param sortingMode : asc , desc
 * @param filters
 * @param search : search term
 * @returns
 */

export const setAppLoading =
  (loading: boolean): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setAppLoading(loading));
  };

export const setLoading =
  (loading: boolean): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setLoading(loading));
  };

export const setViewInStore =
  (newView: ViewTypes): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setView(newView));
  };
export const setCompetitorInStore =
  (newPlans: CompetitorType[]): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setCompetitorArray(newPlans));
  };
export const setGimmickyInStore =
  (newPlan: GimmickyType): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setGimmicky(newPlan));
  };

export const setShowBaseGraphButtonInStore =
  (showButton: boolean): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setShowBaseGraphButton(showButton));
  };

export const setShowRateGraphInStore =
  (showRateGrpah: boolean): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setShowShowRateGraph(showRateGrpah));
  };

export const initialLoad =
  (
    type,
    zipcode,
    terms,
    is_competitor,
    promocode?,
    callback?: Function
  ): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const state = getState().bkv;
    try {
      await initialize(() => {
        if (!zipcode || !type) {
          throw new Error("Missing url parameters!!");
        }
        if (state.gimmicky) {
          if (state.appLoading) {
            dispatch(setAppLoading(false));
          }
        }
        // else if (!state.type) {
        //     // dispatch(getScreenContentInStore(type));
        // }
        else {
          dispatch(slice.actions.setAppLoading(true));
          dispatch(getScreenContentInStore(type));
          dispatch(
            getBluebonnetPlan(type, zipcode, terms, is_competitor, promocode)
          );
          dispatch(slice.actions.setZIPcode(zipcode));
          updateCustomer({ zipcode });
        }
      });
      callback && callback();
    } catch (e) {
      Notibar.error(e);
    }
  };

export const setAddressInStore =
  (address): AppThunk =>
  async (dispatch): Promise<void> => {
    address && dispatch(slice.actions.setElementLoading({ address: true }));
    dispatch(slice.actions.setAddress(address));
    if (address) {
      dispatch(getPremiseDetails(address));
      dispatch(slice.actions.setZIPcode(address.zipcode));
      dispatch(
        getUtilityCode(address.zipcode, () =>
          dispatch(slice.actions.setElementLoading({ address: false }))
        )
      );
      updateCustomer({
        zipcode: address.zipcode,
        street: address.street,
        city: address.city,
        state: address.state,
        esiid: address.esiid,
        fullAddress: address.value,
      });
    }
  };

export const setUsageInStore =
  (usage): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setUsage(usage));
  };

export const getScreenContentInStore =
  (type: string): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    if (type !== getState().bkv.type) {
      dispatch(setAppLoading(true));
      getScreenContent(type).then((response) => {
        const _records = response.data;
        // dispatch(setAppLoading(false));
        dispatch(slice.actions.setContent({ type: type, content: _records }));
      });
    }
  };

export const getForecastUsage =
  (callback: Function): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    dispatch(slice.actions.setLoading(true));
    const state = getState().bkv;
    if (
      state.usage &&
      Object.keys(state.usage).length &&
      state.usageInfo.type === "forecast"
    ) {
      //already has forecast usage
      callback();
    } else {
      const response = await fetchForecastUsage(state.address);
      if (response.data.status === 1) {
        const usages: { [key: string]: number } = response.data["usages"];
        dispatch(slice.actions.setUsage(usages));
        if (Object.keys(usages).length) {
          const usageArr = Object.values(usages);
          let annual = 0;
          for (let i = 0; i < usageArr.length; i++) {
            annual += usageArr[i];
          }
          const avg = Number((annual / 12).toFixed(2));
          annual = Number(annual.toFixed(0));
          dispatch(
            slice.actions.setUsageInfo({
              annual: annual,
              month_avg: avg,
              type: "forecast",
            })
          );
          updateCustomer({
            usage_type: "forecast",
          });
        }
      } else {
        Notibar.error("Failed to get estimate usage!!");
      }
      callback();
    }
    dispatch(slice.actions.setLoading(false));
  };

export const getUtilityCode =
  (zipcode, callback?: Function): AppThunk =>
  async (dispatch, getState): Promise<UtilityType> => {
    const state = getState().bkv;
    const utilforzip = state.utilities[zipcode]; // if already exist then set utility code
    if (utilforzip) {
      dispatch(slice.actions.setUtilityCode(utilforzip.utility_code));
      updateCustomer({ utility_code: utilforzip.utility_code });
      callback && callback();
      return utilforzip;
    } else {
      try {
        const res = await fetchUtilityCode(zipcode);
        if (Array.isArray(res.data)) {
          if (res.data.length === 0) {
            Notibar.error("Your zipcode is not available");
          } else {
            dispatch(
              slice.actions.setUtilities({
                zipcode: state.zipcode,
                utilities: res.data[0],
              })
            );
            updateCustomer({ utility_code: res.data[0]["utility_code"] });
            callback && callback();
            return res.data[0];
          }
        }
      } catch (e) {
        Notibar.error(e, "Failed to fetch TDU usage");
      }
    }
  };

export const setSMTUsage =
  (res, callback: Function): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    if (res.status === "1") {
      let data = res.response;
      let usages: { [key: string]: number } = {};
      let annual = 0,
        count = 0;
      if (Array.isArray(data.reads)) {
        data.reads.map((usg: { actual_kwh: string; end_date: "string" }) => {
          if (typeof usg.end_date === "string" && usg.end_date.includes("/")) {
            let month = usg.end_date.split("/")[0];
            try {
              if (typeof usages[month] === "number") {
                usages[month] += parseInt(usg.actual_kwh);
              } else {
                usages[month] = parseInt(usg.actual_kwh);
              }
            } catch {
              usages[month] = 0;
            }
          }
        });
        Object.values(usages).map((actual_kwh) => {
          annual += actual_kwh;
          count++;
        });
        dispatch(slice.actions.setUsage(usages));
        dispatch(
          slice.actions.setUsageInfo({
            annual: annual,
            month_avg: annual / count,
            type: "tdu",
          })
        );
        updateCustomer({
          usage_type: "smt",
          smt_consent_id: data.consent_id,
        });
      } else {
        Notibar.toast("Usage unavailable");
      }
      callback && callback();
    }
  };

export const getTduUsage =
  (form_values, callback: Function): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const state = getState().bkv;
    try {
      const response = await fetchTduUsage(
        state.utilityCode,
        state.address,
        form_values.email
      );
      if (!Array.isArray(response)) {
        Notibar.error("Failed to get usage!! Please try a different method");
        callback && callback();
        return;
      }
      const _usages: {
        actual_kwh: string;
        year: string;
        month: string;
      }[] = response;
      let usages: { [key: string]: number } = {};
      _usages.map((usage) => {
        usages = {
          ...usages,
          [usage.month]: parseInt(usage.actual_kwh),
        };
      });
      dispatch(slice.actions.setUsage(usages));
      if (Object.keys(usages).length) {
        const usageArr = Object.values(usages);
        let annual = 0;
        for (let i = 0; i < usageArr.length; i++) {
          annual += usageArr[i];
        }
        const avg = Number((annual / 12).toFixed(2));
        annual = Number(annual.toFixed(0));
        dispatch(
          slice.actions.setUsageInfo({
            annual: annual,
            month_avg: avg,
            type: "tdu",
          })
        );
        updateCustomer({
          first_name: form_values.firstName,
          last_name: form_values.lastName,
          email: form_values.email,
          usage_type: "tdu",
        });
      }
      callback && callback();
    } catch (e) {
      Notibar.error(e);
    }
  };

export const getBluebonnetPlan =
  (type, zipcode, terms, is_competitor, promocode?): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const state = getState().bkv;

    try {
      const u_res: any = await dispatch(getUtilityCode(zipcode));
      const utilCode = u_res?.utility_code;
      const res = await fetchBluebonnet(zipcode, utilCode, promocode);
      if (Array.isArray(res.data)) {
        const data = res.data;
        const plans = data[0]["plans"];
        dispatch(slice.actions.setAllPlans(plans));
        const bluebonnet = plans["bluebonnet"];
        const defaultPlan: BKVPlanType = bluebonnet.find(
          (p) =>
            parseInt(p["Pricing"][0]["Terms"]) === parseInt(terms) &&
            !p["Title"].includes("Green")
        );

        const greenPlan: BKVPlanType = bluebonnet.find(
          (p) =>
            parseInt(p["Pricing"][0]["Terms"]) === parseInt(terms) &&
            p["Title"].includes("Green")
        );
        const plan = defaultPlan ? defaultPlan : greenPlan;
        const u_res = await dispatch(getUtilityCode(zipcode));
        dispatch(
          slice.actions.setBluebonnet({
            ...plan,
            rate: plan ? plan["Pricing"][0].kWh2000 : "0¢",
          })
        );
        // dispatch(slice.actions.setAppLoading(false));
        if (is_competitor) {
          await getCompetitors(type, u_res["utility_id"], plan).then((res) => {
            let plans: CompetitorType[] = res.data["plans"] ?? [];
            dispatch(setCompetitorInStore(plans));

            const plan = plans[0];
            if (!!plan) {
              let newGimmicky: GimmickyType = {
                ProductId: plan?.Plan_id,
                Title: plan?.Provider_name,
                Terms: Number(plan?.Contract_term),
                kWh500: Number(plan?.Price500),
                kWh1000: Number(plan?.Price500),
                kWh1500: Number(plan?.Price1000),
                kWh2000: Number(plan?.Price1000),
                kWh2500: Number(plan?.Price2000),
                Tiers: plan?.Tiers,
              };
              // console.log(newGimmicky);
              dispatch(setGimmickyInStore(newGimmicky));
            } else {
              dispatch(slice.actions.setAppLoading(false));
            }
          });
        } else {
          dispatch(getGimmickyPlan("", terms));
        }
        // dispatch(slice.actions.setAppLoading(false));
      } else {
        // console.log(res)
        dispatch(slice.actions.setAppLoading(false));
        throw new Error(res["message"] ?? res["Message"] ?? res);
      }
    } catch (e) {
      Notibar.error(e);
      dispatch(slice.actions.setAppLoading(false));
    }
  };

export const getCompetitorPlans =
  (callback?: Function): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const state = getState().bkv;
    if (state.competitorArray.length === 0 && state.bluebonnet) {
      getCompetitors(state.type, state.utilityID, state.bluebonnet).then(
        (res) => {
          let plans: CompetitorType[] = res.data["plans"] ?? [];
          dispatch(setCompetitorInStore(plans));
          dispatch(slice.actions.setAppLoading(false));
          callback && callback(plans[0]);
        }
      );
    }
  };

export const getGimmickyPlan =
  (zipcode?, terms?, promocode?): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const state = getState().bkv;
    try {
      const gimmicky = state.allPlans[state.type];
      // console.log(gimmicky);
      if (gimmicky) {
        if (gimmicky.length) {
          const plan = gimmicky.find((g) => g["Terms"] === parseInt(terms));
          // console.log(plan);
          const format_gimm = {
            ProductId: plan.ProductId,
            Title: plan.Title,
            Terms: plan.Terms,
            kWh500: parseInt(plan.kWh500.replace("¢", "")),
            kWh1000: parseInt(plan.kWh500.replace("¢", "")),
            kWh1500: parseInt(plan.kWh1000.replace("¢", "")),
            kWh2000: parseInt(plan.kWh1000.replace("¢", "")),
            kWh2500: parseInt(plan.kWh2000.replace("¢", "")),
          };
          dispatch(slice.actions.setGimmicky(format_gimm));
        } else {
          dispatch(slice.actions.setGimmicky({}));
        }
      }
    } catch (e) {
      Notibar.error(e);
    }
  };

export const getPremiseDetails =
  (address: AddressType): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const res = await fetchPremiseDetails(address);
      const details = res.data["premise"]["detail"];
      dispatch(slice.actions.setPremiseDetails(details));
    } catch (e) {
      Notibar.error(e);
    }
  };

export const { reducer } = slice;
export default slice;
